import type { LegendComponentOption } from 'echarts/components';

import { DATALAB_SPLIT_STR } from '@dbl/core/global';
import { BROWSER_LANGUAGE } from '@gp/constants/const';
import { LIGHT_COLOR_ARR } from '../../_config/_constant';
import { LegendPositionEnum } from '@customComp/Chart/chartConfigs/legend/enums';

export type TLegendColors = Record<string, string>;

interface IResolveSeriesColorsParams {
  legendData?: string[];
  legendColors?: TLegendColors;
  palette?: string[];
}

type TLegendFormatter = Extract<LegendComponentOption['formatter'], (value: string) => string>;

/**
 * 图例文本格式化:
 * - 只保留分隔符前的主标题;
 * - 去掉换行与用于内部拼接的拖尾符号, 避免影响图例布局与换行计算。
 */
export const legendTextFormatter: TLegendFormatter = (value: string): string => {
  const text = value?.split(DATALAB_SPLIT_STR)?.[0];
  return text?.replace(/\n|@$/g, '') ?? '';
};

/**
 * 统一构建图例基础样式配置, 保证各图表图例的默认交互与布局一致。
 * 调用方只需透传差异化配置项, 其余维度使用这里的稳态默认值。
 */
export const getLegendConfig = (config: Partial<LegendComponentOption> = {}): LegendComponentOption => {
  const { itemStyle, textStyle, formatter, ...rest } = config;

  return {
    itemStyle: {
      opacity: 1, // 确保不透明, 必须设置, 否则图形不显示
      ...itemStyle,
    },
    left: 'center',
    bottom: -6,
    type: 'scroll',
    icon: 'circle', // 修改icon形状
    inactiveBorderWidth: 0, // 图例关闭时icon圆点的描边粗细
    itemHeight: 8,
    itemWidth: 8,
    itemGap: 20, // 修改每个图例的间距
    pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
    pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
    pageIconSize: 12, // 翻页按钮大小
    pageButtonPosition: 'end', // start
    textStyle: {
      // 图例文字的样式
      color: '#838697',
      fontSize: 12,
      lineHeight: 16,
      padding: [2, 0, 0, 0],
      ...textStyle,
    },
    // 删除图例文本中的换行符, 避免图例位置不对
    formatter: formatter ?? legendTextFormatter,
    ...rest,
  };
};

/**
 * 根据图例项名称与用户自定义颜色表, 生成系列颜色数组:
 * - 若 legendColors 中存在指定名称的覆盖色, 优先使用覆盖值;
 * - 否则按调色板顺序循环取色, 调色板为空时退化为单一安全色。
 */
export const resolveSeriesColorsFromLegendColors = (
  params: IResolveSeriesColorsParams,
): string[] => {
  const { legendData, legendColors, palette = LIGHT_COLOR_ARR } = params;

  if (!Array.isArray(legendData) || legendData.length === 0) {
    // 当图例数据缺失时, 直接回退到完整调色板, 由上层自行截断/复用。
    return palette;
  }

  const safePalette = palette.length > 0 ? palette : ['#5086F3'];

  return legendData.map((name, index) => {
    const override = legendColors?.[name];
    if (override) return override;
    const color = safePalette[index % safePalette.length];
    return color ?? safePalette[0];
  });
};

const LegendLocationMap = {
  [LegendPositionEnum.Below]: {
    legend: {
      bottom: -6,
      top: undefined,
      show: true,
    },
    grid: {
      left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
      top: 22,
      right: 4,
      bottom: 25,
      containLabel: true,
    },
  },
  [LegendPositionEnum.Above]: {
    legend: {
      top: -5,
      bottom: undefined,
      show: true,
    },
    grid: {
      left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
      top: 32,
      right: 4,
      bottom: 6,
      containLabel: true,
    },
  },
  [LegendPositionEnum.Hide]: {
    legend: {
      show: false,
    },
    grid: {
      left: BROWSER_LANGUAGE?.startsWith('en-') ? 6 : 4,
      top: 22,
      right: 4,
      bottom: 6,
      containLabel: true,
    },
  },
};

// 根据图例配置的位置, 返回图例与 grid 的联动布局配置
export const getLegendLocation = ({ position }: { position: LegendPositionEnum }) => {
  const locationConfig = LegendLocationMap[position];
  return { ...locationConfig };
};

export const changeLegendLocation = ({
  option,
  position,
}: {
  option: any;
  position: LegendPositionEnum;
}) => {
  if (!LegendLocationMap[position]) return;
  const { legend, grid } = LegendLocationMap[position];
  if (Array.isArray(option.legend)) {
    option.legend.forEach((item: LegendComponentOption) => {
      Object.assign(item, legend);
    });
  } else {
    Object.assign(option.legend, legend);
  }
  Object.assign(option.grid, grid);
};